import { useCallback, useEffect, useState } from 'react';
import useAuth from './useAuth';
import { getConnectAccountStatus } from 'src/backend/marketplace';

/**
 * Fetches the Stripe Connect onboarding status.
 * Only makes the API call when marketplace is enabled for the user's org.
 * @returns {{
 *   status: 'not_created' | 'incomplete' | 'pending_verification' | 'onboarded' | 'ineligible_business_type' | null,
 *   currentlyDue: boolean,
 *   capabilities: { card_payments: string, transfers: string, can_post_listing: boolean } | null,
 *   loading: boolean,
 *   error: Error | null,
 *   refetch: Function,
 *   setStatus: Function
 * }}
 */
const useConnectOnboardingStatus = () => {
  const { user } = useAuth();
  const enabled = !!user.marketplace_enabled;
  const [status, setStatus] = useState(null);
  const [currentlyDue, setCurrentlyDue] = useState(false);
  const [capabilities, setCapabilities] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);
  const [fetchKey, setFetchKey] = useState(0);

  const refetch = useCallback(() => {
    setFetchKey((current) => current + 1);
  }, []);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    getConnectAccountStatus()
      .then((data) => {
        if (!cancelled) {
          setStatus(data.status);
          setCurrentlyDue(!!data.currently_due);
          setCapabilities(data.capabilities || null);
          setMessage(data.message || '');
        }
      })
      .catch((fetchError) => {
        console.error(fetchError);
        if (!cancelled) {
          setError(fetchError);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, fetchKey]);

  return {
    status,
    currentlyDue,
    capabilities,
    message,
    loading,
    error,
    refetch,
    setStatus,
  };
};

export default useConnectOnboardingStatus;
